import { cn } from "@/lib/utils";
import { Heart, Sparkles } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface CompatibilityBadgeProps {
  score: number;
  sharedInterests: string[];
  totalShared: number;
  size?: "sm" | "md";
  className?: string;
}

export const CompatibilityBadge = ({
  score,
  sharedInterests,
  totalShared,
  size = "sm",
  className,
}: CompatibilityBadgeProps) => {
  if (score === 0 && totalShared === 0) {
    return null; // Don't show if no data
  }

  const getScoreColor = () => {
    if (score >= 70) return "text-green-500 bg-green-500/15 border-green-500/30";
    if (score >= 40) return "text-amber-500 bg-amber-500/15 border-amber-500/30";
    return "text-muted-foreground bg-muted/50 border-border";
  };

  const getScoreLabel = () => {
    if (score >= 70) return "Great match!";
    if (score >= 40) return "Good match";
    return "Some common ground";
  };

  const isSm = size === "sm";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "inline-flex items-center gap-1 rounded-full border font-medium cursor-help",
              getScoreColor(),
              isSm ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-xs",
              className
            )}
          >
            {score >= 70 ? (
              <Sparkles className={cn(isSm ? "w-2.5 h-2.5" : "w-3 h-3")} />
            ) : (
              <Heart className={cn(isSm ? "w-2.5 h-2.5" : "w-3 h-3")} />
            )}
            <span>{score}%</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[200px]">
          <p className="font-semibold text-sm">{getScoreLabel()}</p>
          {sharedInterests.length > 0 && (
            <div className="mt-2">
              <p className="text-xs text-muted-foreground mb-1.5">Shared interests:</p>
              <div className="flex flex-wrap gap-1.5">
                {sharedInterests.slice(0, 5).map((interest, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium animate-in fade-in-0 zoom-in-95"
                    style={{ animationDelay: `${idx * 0.05}s` }}
                  >
                    <Heart className="w-2.5 h-2.5" />
                    {interest}
                  </span>
                ))}
                {totalShared > sharedInterests.length && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px] font-medium">
                    +{totalShared - sharedInterests.length} more
                  </span>
                )}
              </div>
            </div>
          )}
          {sharedInterests.length === 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              Add interests to see compatibility
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
